import React, {useState} from 'react';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {API_URL} from '../constants';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {DoneAll} from '@mui/icons-material';
import BidmiiTextField from '../common/StyledComponents/BidmiiTextField';
import {theme} from '../Theme';
import PrimaryButton from '../common/StyledComponents/PrimaryButton';
import * as Sentry from '@sentry/react';

interface IProps {
  setLoading: any;
  price: string | number | null;
  code?: string | null;
}

export default function useDiscountCode(props: IProps) {
  const {setLoading, price, code} = props;

  const [discountCode, setDiscountCode] = useState<string>(code ?? '');
  const [incorrectCode, setIncorrectCode] = useState<string | null>(null);
  const [discount, setDiscount] = useState<number>(0.0);
  const [referralValid, setReferralValid] = useState(false);

  const validateDiscountCode = () => {
    if (discountCode) {
      setLoading(true);
      securedFetchRequest(`${API_URL}/code/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: discountCode,
        }),
      })
        .then((res) => res.json())
        .then((res) => {
          setLoading(false);
          if (res?.valid) {
            setIncorrectCode(null);

            if (res?.type === 'REFERRAL') {
              setReferralValid(true);
              setDiscount(Number(price));
            }
            if (res?.type === 'FIXED') {
              setDiscount(Number(res.amount));
            }
            if (res?.type === 'PERCENT') {
              setDiscount(Number(price) * (res.amount / 100));
            }
          } else {
            setDiscount(0);
            setIncorrectCode(
              res.message ?? 'This code cannot be applied to your order.',
            );
          }
        })
        .catch((err) => {
          process.env.NODE_ENV === 'development' && console.error(err);
          Sentry.withScope(function (scope) {
            scope.setTag(
              'section',
              'useDiscountCode.tsx validateDiscountCode()',
            );
            Sentry.captureException(err);
          });
          setLoading(false);
          setDiscount(0);
          setIncorrectCode('This code cannot be applied to your order.');
        });
    }
  };

  const DiscountCodeInput = (
    <Box mb={3} style={{position: 'relative'}}>
      <Grid container direction={'row'} alignItems="center">
        <Typography variant="subtitle2">Discount Code</Typography>
        {discount > 0 && (
          <DoneAll
            fontSize="small"
            style={{color: theme.palette.green.main, marginLeft: 6}}
          />
        )}
      </Grid>
      <Grid container justifyContent="space-between">
        <Grid item xs={true} style={{marginRight: 12}}>
          <BidmiiTextField
            fullWidth
            placeholder="Enter discount code"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value)}
          />
        </Grid>
        <PrimaryButton onClick={validateDiscountCode} disabled={!discountCode}>
          APPLY
        </PrimaryButton>
      </Grid>
      {incorrectCode && (
        <Typography
          variant="caption"
          style={{
            position: 'absolute',
            color: theme.palette.red.dark,
            marginTop: 2,
          }}>
          {incorrectCode}
        </Typography>
      )}
    </Box>
  );

  return [DiscountCodeInput, discountCode, discount, referralValid];
}
